<template>
  <div class='lt-fyf-result-page'>
    <!-- Chosen Retirement Living Standard banner -->
    <div class='lt-fyf-result-page_standard-chosen'>
      <div class='standard-values standard-values--minimum'>
        <span class='standard-values__text'>{{ standards.minimum.text }} </span>
        <span class='standard-values__figure'>{{ config.headlineFigures[isCohabiting ? 'cohabiting' : 'single'].minimum | currency(config.currencySymbol) }}</span>
      </div>
      <div class='standard-values standard-values--moderate'>
        <span class='standard-values__text'>{{ standards.moderate.text }} </span>
        <span class='standard-values__figure'>{{ config.headlineFigures[isCohabiting ? 'cohabiting' : 'single'].moderate | currency(config.currencySymbol) }}</span>
      </div>
      <div class='standard-values standard-values--maximum'>
        <span class='standard-values__text'>{{ standards.comfortable.text }} </span>
        <span class='standard-values__figure'>{{ config.headlineFigures[isCohabiting ? 'cohabiting' : 'single'].comfortable | currency(config.currencySymbol) }}</span>
      </div>
    </div>

    <!-- Chosen Retirement Living Standard arrow -->
    <div class='target-bar'>
      <div
        class='target-triangle'
        :style="{ left: `${triangleBarLeftCSSValue}%`, transform: `translateX(-${triangleBarLeftCSSValue}%)`}"
      ></div>
    </div>

    <div class='lt-fyf-result-page_content_container'>
      <ProgressBar class='lt-fyf-progress-bar--large' />

      <!-- Initial overview -->
      <div class='lt-fyf-result-page_top_content'>
        <div class='lt-fyf-result-overview'>
          <div class='lt-fyf-result-overview_left lt-fyf-result-page_columns_text'>
            <p class='lt-fyf-result-page_top_content_main-para'>
              <RenderTemplate :string="copy.target" />
            </p>
          </div>
          <div class='lt-fyf-result-overview_center'>
            <figure class="lt-fyf-result-page_target_figure">
              <AnimatedNumber
                :value='targetIncome | roundToNearestHundred'
                :duration='0.5'
                :prefix='config.currencySymbol'
              />
              a year
            </figure>
          </div>
          <div class='lt-fyf-result-overview_right'>
            <button aria-label="Read more about how this is calculated" class="lt-fyf-result-page_target_button" @click="handleReadMoreClick('readMoreTargetVisible', 'readMoreTargetContent')">
              Read {{ readMoreTargetVisible ? 'less' : 'more' }}
            </button>
          </div>
        </div>

        <!-- Read More box -->
        <div class='lt-fyf-result-page_columns'>
          <div class='lt-fyf-result-page_columns_left'>
            <transition name="height">
              <div
                class='lt-fyf-result-page_read-more_target_content'
                v-if="readMoreTargetVisible"
                aria-live="polite"
                tabindex="1"
                ref="readMoreTargetContent"
              >
                <hr class='lt-fyf-result-page_seperator' />
                <p>
                  <RenderTemplate :string="copy.readMoreTarget" />
                </p>
              </div>
            </transition>
          </div>

          <div class='lt-fyf-result-page_columns_right read-more-container'>
          </div>
        </div>
      </div>

      <!-- Additional information -->
      <div class='lt-fyf-result-page_content'>
        <div class='lt-fyf-result-page_columns'>
          <div class='lt-fyf-result-page_columns_left middle-box'>
            <div class='lt-fyf-result-page_inputs'>
              <h3 class='lt-fyf-result-page_inputs_header'>
                <RenderTemplate :string="copy.inputsHeader" />
              </h3>
              <div :class="['lt-fyf-pd-field', isAgeValid ? '' : 'lt-fyf-pd-field--is-error']">
                <label for='lt-fyf-age'>What's your age?</label>
                <VueAutonumeric
                  id='lt-fyf-age'
                  v-model='age'
                  :options='currentAgeAutoNumericOpts'
                  pattern="[0-9]*"
                />
              </div>

              <div :class="['lt-fyf-pd-field', isCurrentBalanceValid ? '' : 'lt-fyf-pd-field--is-error']">
                <label for='lt-fyf-balance'>{{ isCohabiting ? 'Pension savings already built up by you and your partner?' : 'Pension savings already built up?' }}</label>

                <VueAutonumeric
                  id='lt-fyf-balance'
                  v-model='currentBalance'
                  :options="currentBalanceAutoNumericOpts"
                  pattern="[0-9]*"
                />
              </div>

              <div v-if="config.canChangeRetirementAge" :class="['lt-fyf-pd-field', isRetirementAgeValid ? '' : 'lt-fyf-pd-field--is-error']">
                <label for='lt-fyf-ret-age'>Age you want to retire at?</label>
                <VueAutonumeric
                  id='lt-fyf-ret-age'
                  v-model='retirementAge'
                  :options='retirementAgeAutoNumericOpts'
                  pattern="[0-9]*"
                />
              </div>

              <div v-else class="lt-fyf-pd-field">
                <label for='lt-fyf-ret-age'>We'll assume you retire at age {{ retirementAge }}</label>
              </div>

              <div class='lt-fyf-result-page_target-pm' v-if="!isOnTrack">
                <RenderTemplate class="lt-fyf-result-page_target-pm_text" :string="copy.figureAmountNeeded" />
                <div class='large-arrow'></div>
                <figure class="lt-fyf-result-page_target-pm_figure">
                  <AnimatedNumber
                    :value='resultPerMonth'
                    :duration='0.8'
                    :prefix='config.currencySymbol'
                    v-if='areInputsValid'
                  />
                  <span>per month</span>
                  <template v-if='!areInputsValid'>
                    -
                  </template>
                </figure>
              </div>

              <div class='lt-fyf-result-page_target-pm' v-else>
                <RenderTemplate :string="copy.isOnTrack" />
              </div>


              <transition name="height">
                <div
                  class='lt-fyf-result-page_read-more_amount-needed_content'
                  aria-live="polite"
                  tabindex="-1"
                  ref="readMoreAmountContent"
                >
                  <RenderTemplate :string="copy.readMoreAmountNeeded" />
                </div>
              </transition>

              <button
                class='refresh-button'
                @click='reload'
              >
                Start Over
              </button>
            </div>
          </div>

          <div class='lt-fyf-result-page_columns_right lt-fyf-result-page_columns_right--stretch'>
            <div class='lt-fyf-result-page_next_steps_content'>
              <p class='lt-fyf-result-page_scbs_calculator_para'>
                To find out how much goes into your pension and how much more you could get, visit our <a href="https://experience200.ehr.com/lloydsbankinggrouppensions/s/save-and-invest/contribute" target="_parent">contributions page</a>.
              </p>
              <p class='lt-fyf-result-page_scbs_calculator_para'>
                You can change how much you’re saving in your online account. Log into Your Pension, select ‘Your Pension’ on the menu, then ‘Contribution Change’.
              </p>
              <p class='lt-fyf-result-page_scbs_calculator_para'>
                Remember, you can increase or decrease how much you save at any time.
              </p>
              <div class='ahc-small-change-link-container'>
                <a
                  href='https://epa.towerswatson.com/accounts/lbg/'
                  class='ahc-small-change-link'
                  target='_blank'
                >Log into Your Pension</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { mapFields } from "vuex-map-fields";

const ageAutoNumericOpts = {
  decimalPlaces: 0,
  wheelStep: 1,
};

export default {
  data () {
    return {
      readMoreAmountVisible: false,
      readMoreTargetVisible: false,
    };
  },
  computed: {
    copy() {
      return {
        inputsHeader: `Now find out how to hit your target...`,
        targetHeader: `Your target figure`,
        target: `To afford your chosen lifestyle in retirement, ${this.isCohabiting ? 'you and your partner will' : 'you\'ll'} need in the region of`,
        readMoreTarget: `Your target figure is the amount of expenditure ${this.isCohabiting ? 'you and your partner will' : 'you\'ll'} need to cover your chosen lifestyle in retirement. This expenditure amount has been calculated based on research carried out by the PLSA as part of their 'Retirement Living Standards' initiative. See the <a href='#' @click.prevent="$store.commit('goToPage', 'disclaimer-and-assumptions')">Disclaimer and Assumptions</a> for more detail.`,
        figureAmountNeeded: `To reach this target you ${this.isCohabiting ? 'and your partner' : ''} should aim to be saving`,
        isOnTrack: `You are on track to reach your target.`,
        readMoreAmountNeeded: `
        ${
          this.isOnTrack ?
            `<p>Your expenditure target will be covered by your ${this.isCohabiting ? 'combined' : ''} State Pension payment of {{ $store.getters.statePension | currency(($store.state.config.currencySymbol)) }} a year and any existing pension savings. </p>`
          :
            `<p>This is the amount ${this.isCohabiting ? 'you and your partner' : 'you'} would need to save each month in order to have enough income to meet your net expenditure target of {{ $store.getters.targetIncome | currency($store.state.config.currencySymbol) }} per year.</p>
            <p>It is based on ${this.isCohabiting ? 'you and your partner' : 'you'} needing a gross annual income in retirement of {{ $store.getters.grossIncomeNeeded | currency($store.state.config.currencySymbol) }} + ${this.isCohabiting ? 'combined' : ''} State Pension of {{ $store.getters.statePension | currency(($store.state.config.currencySymbol)) }}.</p>
            <p>This figure is a total ${this.isCohabiting ? "'household'" : ''} savings amount, so could also be made up of any pension contributions from your employer. It also assumes you will be entitled to the full State Pension when you retire.</p>`
        }

        <p>See the <a href='#' @click.prevent="$store.commit('goToPage', 'disclaimer-and-assumptions')">Disclaimer and Assumptions</a> for more detail.</p>`,
        smallChangeBigSavings: `It can be easier than you think to save more money for your retirement. Explore your options using our Small Change, Big Savings calculator.`,
      }
    },

    ...mapState([
      'config',
    ]),
    ...mapFields({
      age: 'config.inputs.age.currentValue',
      retirementAge: 'config.inputs.retirementAge.currentValue',
      currentBalance: 'config.inputs.currentBalance.currentValue',
      isCohabiting: 'engine.isCohabiting',
    }),
    ...mapGetters([
      'areInputsValid',
      'targetIncome',
      'resultPerMonth',
      'standardChosen',
      'isAgeValid',
      'isRetirementAgeValid',
      'isCurrentBalanceValid',
      'maxTargetIncome',
      'standards',
      'isOnTrack'
    ]),

    currentAgeAutoNumericOpts: function () {
      return _.defaults({
        minimumValue: 0,
        maximumValue: this.config.inputs.age.max,
      }, ageAutoNumericOpts);
    },

    retirementAgeAutoNumericOpts: function () {
      return _.defaults({
        minimumValue: 0,
        maximumValue: this.config.inputs.retirementAge.max,
      }, ageAutoNumericOpts);
    },

    currentBalanceAutoNumericOpts: function () {
      return {
        currencySymbol: this.config.currencySymbol,
        minimumValue: '0',
        allowDecimalPadding: false,
        emptyInputBehavior: 'min',
        minimumValue: this.config.inputs.currentBalance.min,
        maximumValue: this.config.inputs.currentBalance.max,
      }
    },
    triangleBarLeftCSSValue() {
      const range = this.standards.comfortable.maxValue - this.standards.minimum.maxValue;
      const percentage = ((this.targetIncome - this.standards.minimum.maxValue) / range * 100);
      return percentage;
    },
  },
  methods: {
    toggleBool(bool) {
      if (this[bool]) {
        this[bool] = false;
        return
      }
      this[bool] = true;
    },
    reload () {
      window.location.reload();
    },
    handleReadMoreClick(key, ref) {
      this.toggleBool([key]);
      if (this[key]) {
        this.$nextTick(() => {
          this.$refs[ref].focus(); // for accessibility
        });
      }
    }
  }
};
</script>

<style lang='scss'>
.lt-fyf-result-page_inputs_header {
  color: #00864F;
  padding-top: 20px;
}
.lt-fyf-result-page_target_button,
.lt-fyf-result-page_amount_needed_button, .ahc-small-change-link-container a {
  display: inline-block;
  padding: 0.5rem 1rem;
  border: 3px solid #00864f;
  color: #00864f;
  margin: 1rem 0 0 0;
  text-decoration: none;
  cursor: pointer;
  background: white;
  font-size: 1rem;
  min-width: 124px;
  border-radius: 0;
  &:hover {
    background: #00864f;
    color: white;
  }
}

.lt-fyf-result-page_read-more_amount-needed_content {
  padding: 0;
}

.lt-fyf-pd-field input {
  font-size: 1.4rem;
  max-width: 8.75rem;
  width: 100%;
  font-weight: 700;
  text-align: center;
  padding: 0.25rem 0;
  border: 0 none;
  background: #fff;
  border-radius: 0;
  border: 1px solid #383738;
}

.refresh-button {
  background: transparent url('../assets/start-over.svg') no-repeat center left;
  background-size: 1.125rem;
}

.large-arrow {
  background-size: 25px 48px;
}
</style>
